import { useEnterAnimation } from "@/pages/services/hooks/useEnterAnimation";

/**
 * AboutStory — the origin story block.
 *
 * Two-column layout:
 *   · Left: large square image (candle-lit desk / notebook still life)
 *   · Right: numbered chapter "02", big headline, and 2 paragraphs of story
 *
 * A small "since 2016" timeline strip anchors the bottom.
 */
export default function AboutStory() {
  const eyebrowRef = useEnterAnimation<HTMLDivElement>({ delay: 0 });
  const imageRef = useEnterAnimation<HTMLDivElement>({ delay: 80 });
  const titleRef = useEnterAnimation<HTMLHeadingElement>({ delay: 200 });
  const p1Ref = useEnterAnimation<HTMLParagraphElement>({ delay: 360 });
  const p2Ref = useEnterAnimation<HTMLParagraphElement>({ delay: 520 });
  const timelineRef = useEnterAnimation<HTMLDivElement>({ delay: 700 });

  return (
    <section
      id="about-story"
      className="relative w-full bg-background-100"
      style={{ minHeight: "100vh" }}
    >
      {/* Section meta */}
      <div className="absolute top-0 left-0 right-0 z-30 px-6 md:px-10 pt-14 md:pt-16 pointer-events-none">
        <div className="max-w-[1600px] mx-auto flex items-baseline justify-between">
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
            About Us / 02
          </span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium hidden md:block">
            Chapter 02 · Our story
          </span>
        </div>
      </div>

      <div className="w-full min-h-screen flex items-center px-6 md:px-10 py-28 md:py-36">
        <div className="max-w-[1400px] w-full mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div ref={imageRef} className="lg:col-span-6 order-2 lg:order-1">
            <div
              className="relative w-full overflow-hidden rounded-2xl"
              style={{ aspectRatio: "1 / 1" }}
            >
              <img
                src="https://images.pexels.com/photos/1497206/pexels-photo-1497206.jpeg?auto=compress&cs=tinysrgb&w=1600&h=1600&fit=crop"
                alt="A warm still life of an open notebook, fountain pen and lit candle on a wooden desk"
                title="Where the story began — a notebook, a candle, a question"
                className="absolute inset-0 w-full h-full object-cover object-center select-none"
                draggable={false}
              />
            </div>
          </div>

          {/* Text */}
          <div className="lg:col-span-6 order-1 lg:order-2 flex flex-col">
            <div ref={eyebrowRef} className="mb-6 flex items-center gap-3">
              <span
                className="font-heading"
                style={{
                  fontSize: "clamp(2.75rem, 4vw, 4rem)",
                  fontWeight: 300,
                  letterSpacing: "-0.03em",
                  color: "oklch(var(--foreground-950) / 0.35)",
                  lineHeight: 1,
                  fontVariationSettings: "'opsz' 144",
                }}
              >
                02
              </span>
              <span className="w-12 h-[1px] bg-foreground-950/25"></span>
              <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
                Our story
              </span>
            </div>

            <h2
              ref={titleRef}
              className="mb-8"
              style={{
                fontFamily: "var(--font-heading)",
                fontSize: "clamp(2.5rem, 5vw, 5rem)",
                fontWeight: 300,
                letterSpacing: "-0.025em",
                lineHeight: 1.05,
                color: "oklch(var(--foreground-950))",
                fontVariationSettings: "'opsz' 144",
              }}
            >
              We started small.
              <br />
              <span className="italic text-foreground-950/85">
                And we stayed small.
              </span>
            </h2>

            <p
              ref={p1Ref}
              className="mb-6 max-w-[520px]"
              style={{
                fontFamily: "var(--font-body)",
                fontSize: "clamp(1rem, 1.1vw, 1.15rem)",
                lineHeight: 1.75,
                color: "oklch(var(--foreground-950) / 0.85)",
              }}
            >
              Serene began in the autumn of 2016, with one therapist, one
              armchair, and a single line written in a notebook &mdash;
              &ldquo;a room where nobody has to perform.&rdquo; Nine years
              later, we are still writing the same line.
            </p>

            <p
              ref={p2Ref}
              className="mb-12 max-w-[520px]"
              style={{
                fontFamily: "var(--font-body)",
                fontSize: "clamp(1rem, 1.1vw, 1.15rem)",
                lineHeight: 1.75,
                color: "oklch(var(--foreground-950) / 0.75)",
              }}
            >
              We chose to stay a small studio &mdash; three therapists, one
              waiting corner, one kettle &mdash; so that every person who
              walks in is met by name and not by a number on a schedule.
            </p>

            {/* Tiny timeline strip */}
            <div ref={timelineRef} className="flex flex-wrap items-center gap-8">
              {[
                { year: "2016", label: "The first session" },
                { year: "2019", label: "Second therapist joined" },
                { year: "2022", label: "Online sessions opened" },
                { year: "2025", label: "You are here" },
              ].map((m, i) => (
                <div key={m.year} className="flex items-center gap-3">
                  <div className="flex items-center gap-2.5">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{
                        background:
                          i === 3
                            ? "oklch(var(--primary-500))"
                            : "oklch(var(--foreground-950) / 0.35)",
                      }}
                    ></span>
                    <span
                      className="font-heading"
                      style={{
                        fontSize: "18px",
                        fontWeight: 400,
                        letterSpacing: "-0.01em",
                        color: "oklch(var(--foreground-950))",
                      }}
                    >
                      {m.year}
                    </span>
                    <span
                      className="uppercase font-medium text-foreground-950/60"
                      style={{ fontSize: "10px", letterSpacing: "0.28em" }}
                    >
                      {m.label}
                    </span>
                  </div>
                  {i < 3 ? (
                    <span className="w-6 h-[1px] bg-foreground-950/25 hidden md:block"></span>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}